
import React, { useCallback } from 'react';
import { UploadCloud, Image as ImageIcon } from 'lucide-react';

interface FileUploadProps {
  onFileSelect: (file: File) => void;
  onFilesSelect?: (files: File[]) => void;
  label?: string;
  subLabel?: string;
  accept?: string;
  previewUrl?: string | null;
  multiple?: boolean;
}

export const FileUpload: React.FC<FileUploadProps> = ({ 
  onFileSelect, 
  onFilesSelect,
  label = "Click to upload image",
  subLabel = "JPG, PNG up to 10MB",
  accept = "image/*",
  previewUrl,
  multiple = false
}) => {
  const handleFileChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      if (multiple && onFilesSelect) {
        onFilesSelect(Array.from(e.target.files));
      } else {
        onFileSelect(e.target.files[0]);
      }
      e.target.value = ''; // Reset input to allow selecting same file again
    }
  }, [onFileSelect, onFilesSelect, multiple]);

  return (
    <div className="w-full">
      <label className={`
        relative flex flex-col items-center justify-center w-full 
        transition-all duration-200
        border-2 border-dashed rounded-xl cursor-pointer
        ${previewUrl ? 'border-primary-300 bg-slate-50 p-4' : 'border-slate-300 bg-white hover:bg-slate-50 h-64'}
      `}>
        {previewUrl && !multiple ? (
          <div className="relative w-full flex items-center justify-center overflow-hidden group">
            {/* allow flex height for vertical images */}
            <img 
              src={previewUrl} 
              alt="Preview" 
              className="max-w-full max-h-[60vh] object-contain rounded-lg shadow-sm" 
            />
            <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center rounded-lg">
              <p className="text-white font-medium flex items-center gap-2 backdrop-blur-sm bg-black/30 px-4 py-2 rounded-full">
                <UploadCloud size={20} /> Change Image
              </p>
            </div>
          </div>
        ) : (
          <div className="flex flex-col items-center justify-center pt-5 pb-6 text-center p-4">
            <div className="mb-4 p-3 bg-primary-50 rounded-full text-primary-600">
              <UploadCloud size={24} />
            </div>
            <p className="mb-2 text-sm font-semibold text-slate-700">{label}</p>
            <p className="text-xs text-slate-500">{subLabel}</p>
          </div>
        )}
        <input 
          type="file" 
          className="hidden" 
          accept={accept} 
          multiple={multiple}
          onChange={handleFileChange} 
        />
      </label>
    </div>
  );
};